import { firestore } from '../config/fbConfig';
import { useCallback, useState, useRef } from 'react';

const useQueryServices = (name, defaultOrder) => {
  const LIMIT_NUMBER = 4;
  const COLLECTION_NAME = name;

  const collectionRef = useRef(null);
  const filtersRef = useRef([]);

  const orderValueRef = useRef(defaultOrder);
  const [documents, setDocuments] = useState([]);

  // variables for paginating query
  const lastDoc = useRef(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [noMoreResults, setNoMoreResults] = useState(false);

  const resetToDefault = useCallback(() => {
    lastDoc.current = null;
    setDocuments([]);
    collectionRef.current = firestore.collection(COLLECTION_NAME);
    setLoadingMore(false);
    setNoMoreResults(false);
  }, [COLLECTION_NAME]);

  const getAllDocuments = useCallback(
    async () => {
      try {
        const querySnapshot = await collectionRef.current.get();

        let tempDocs = [];

        querySnapshot.forEach((doc) => {
          tempDocs.push({ id: doc.id, ...doc.data() });
        });

        setDocuments([...documents, ...tempDocs]);
      } catch (error) {
        console.error(error);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const getNextDocuments = useCallback(async () => {
    let startAfterDocNumber = 0;

    if (lastDoc && lastDoc.current) {
      startAfterDocNumber = lastDoc.current;
    }

    try {
      let querySnapshot;

      if (collectionRef && collectionRef.current) {
        querySnapshot = await collectionRef.current.startAfter(startAfterDocNumber).limit(LIMIT_NUMBER).get();

        // check if the end of documents is reach
        if (querySnapshot.size < LIMIT_NUMBER) {
          setLoadingMore(false);
          setNoMoreResults(true);
        }

        lastDoc.current = querySnapshot.docs[querySnapshot.docs.length - 1];

        let tempDocs = [];

        querySnapshot.forEach((doc) => {
          tempDocs.push({ id: doc.id, ...doc.data() });
        });

        setDocuments([...documents, ...tempDocs]);
      }
    } catch (error) {
      console.error(error);
    }
  }, [documents]);

  const loadAllDocuments = useCallback(
    (paginate) => {
      resetToDefault();
      collectionRef.current = collectionRef.current.orderBy(orderValueRef.current);

      if (paginate) {
        getNextDocuments();
        setLoadingMore(true);
      } else {
        getAllDocuments();
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const loadFilteredDocuments = useCallback(
    (paginate) => {
      resetToDefault();

      // dynically add where clause(s) to query
      filtersRef.current.forEach((filter) => {
        collectionRef.current = collectionRef.current.where(filter.key, filter.condition, filter.value);
      });

      collectionRef.current = collectionRef.current.orderBy(orderValueRef.current);

      if (paginate) {
        getNextDocuments();
        setLoadingMore(true);
      } else {
        getAllDocuments();
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const loadSearchDocument = useCallback(async (fieldName, conditionName, compareValue) => {
    resetToDefault();

    try {
      const querySnapshot = await collectionRef.current.where(fieldName, conditionName, compareValue).get();

      const tempDocs = [];

      querySnapshot.forEach((doc) => {
        tempDocs.push({ id: doc.id, ...doc.data() });
      });

      setDocuments(tempDocs);
    } catch (error) {
      console.error(error);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadDocumentsWithNewSort = useCallback(
    (paginate, newOrder) => {
      resetToDefault();

      // dynically add where clause(s) to query
      if (filtersRef.current.length > 0) {
        filtersRef.current.forEach((filter) => {
          collectionRef.current = collectionRef.current.where(filter.key, filter.condition, filter.value);
        });
      }

      collectionRef.current = collectionRef.current.orderBy(newOrder);

      if (paginate) {
        getNextDocuments();
        setLoadingMore(true);
      } else {
        getAllDocuments();
      }

      orderValueRef.current = newOrder;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const addToFilters = useCallback((fieldName, conditionName, compareValue) => {
    // check if there are other filters already
    if (filtersRef.current.length > 0) {
      // check if the field is already in filters
      const fieldIndex = filtersRef.current.findIndex((filter) => filter.key === fieldName);

      if (fieldIndex >= 0) {
        // if so, update the new compare value for that field
        filtersRef.current[fieldIndex].value = compareValue;
        return;
      }
    }

    // else add new filter
    const updateFilters = [
      ...filtersRef.current,
      {
        key: fieldName,
        condition: conditionName,
        value: compareValue,
      },
    ];

    filtersRef.current = updateFilters;
  }, []);

  const addRangeFilter = useCallback((fieldName, condition1Name, compareValue1, condition2Name, compareValue2) => {
    const updateFilters = [
      ...filtersRef.current,
      {
        key: fieldName,
        condition: condition1Name,
        value: compareValue1,
      },
      {
        key: fieldName,
        condition: condition2Name,
        value: compareValue2,
      },
    ];

    filtersRef.current = updateFilters;
  }, []);

  const removeFilter = useCallback((fieldName) => {
    const updatedFilters = filtersRef.current.filter((filter) => filter.key !== fieldName);
    filtersRef.current = updatedFilters;
  }, []);

  const removeAllFilters = useCallback(() => {
    filtersRef.current = [];
  }, []);

  const setOrderBy = (value) => {
    orderValueRef.current = value;
  };

  return {
    documents,
    loadingMore,
    noMoreResults,
    addToFilters,
    getNextDocuments,
    loadAllDocuments,
    loadFilteredDocuments,
    loadSearchDocument,
    removeFilter,
    removeAllFilters,
    setOrderBy,
    addRangeFilter,
    loadDocumentsWithNewSort,
  };
};

export default useQueryServices;
